import {Component, OnInit} from '@angular/core';
import {ModalInterface} from "../../modal.interface";
import {defer, forkJoin, merge, Observable, of, Subject} from "rxjs";
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {ModalState} from "../../modal-state.enum";
import {ProjectRestService} from "../../../../api/services/project.rest.service";
import {ModalStateEvent} from "../../modal-state-event";
import {RxFormBuilder, RxwebValidators} from "@rxweb/reactive-form-validators";
import {AbstractComponent} from "../../../../core/components/abstract-component";
import {ModalComponent} from "../../modal.component";
import {ProjectApiModel} from "../../../../api/models/project.api-model";
import {TranslateService} from "@ngx-translate/core";
import {MessageService} from "primeng/api";
import {PdfThumbnailService} from "../../../../core/services/pdf-thumbnail.service";
import {DocumentApiModel} from "../../../../api/models/document.api-model";
import {DocumentUploadService} from "../../../../core/services/document-upload.service";
import {ResponseErrorsHandlerService} from "../../../../core/services/response-errors-handler.service";
import {format} from "date-fns";
import {catchError, finalize, ignoreElements, map, switchMap, takeUntil, tap} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
  selector: 'app-add-documents',
  templateUrl: './add-documents.component.html',
  styleUrls: ['./add-documents.component.scss'],
  providers: [MessageService]
})
export class AddDocumentsComponent extends AbstractComponent implements OnInit, ModalInterface {
  public readonly maxDocumentFileSizeBytes = 50 * 1024 * 1024;

  get pendingDocuments(): UntypedFormArray {
    if (this.form && this.form.get('pendingDocuments')) {
      return this.form.get('pendingDocuments') as UntypedFormArray;
    }
  }

  get uploadedDocuments(): UntypedFormArray {
    if (this.form && this.form.get('uploadedDocuments')) {
      return this.form.get('uploadedDocuments') as UntypedFormArray;
    }
  }

  // get pendingDocuments(): AbstractControl[] {
  //   return this.pendingDocuments.controls.filter((control) => !control.value['@id']);
  // }
  //
  // get uploadedDocuments(): AbstractControl[] {
  //   return this.pendingDocuments.controls.filter((control) => control.value['@id']);
  // }

  public data: any;
  public parent?: ModalComponent;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;
  public form: UntypedFormGroup;
  public project: ProjectApiModel;
  public documentsAMs: DocumentApiModel[] = [];
  public files = [];
  public totalSize : number = 0;
  public totalSizePercent : number = 0;
  public uploadProgress: number = 0;
  public documentErrorKey: string | null = null;
  private readonly stopReads = new Subject<void>();
  private submitting = false;
  private initializing = false;
  private destroyed = false;
  constructor(
    private readonly fb: RxFormBuilder,
    private readonly projectRS: ProjectRestService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly pdfThumbnail: PdfThumbnailService,
    private readonly documentUploader: DocumentUploadService,
    private readonly responseErrorsHandler: ResponseErrorsHandlerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.project = this.data['project'].toApiModel();
    this.documentsAMs = this.data['documentAMs'] || [];
    this.createForm();
    this.patchForm();
    this.subscribeParentModalState();
    this.initializing = true;
    const reads = this.documentsAMs.map(document => document['@id']
      ? this.documentUploader.loadReadyDocument(document, 600).pipe(
        catchError(error => of(Object.assign(new DocumentApiModel(), document, {previewErrorKey: this.documentUploader.previewErrorKey(error)}))),
        tap(ready => Object.assign(document, ready))
      ) : of(document));
    this.subscriptions.push((reads.length ? forkJoin(reads) : of([])).pipe(
      takeUntil(this.stopReads),
      finalize(() => {
        if (!this.destroyed) {
          this.initializing = false;
          this.patchForm();
          this.documentErrorKey = this.documentsAMs.find(document => document.previewErrorKey)?.previewErrorKey || null;
          this.subject.next(new ModalStateEvent(this.form.valid ? ModalState.VALID : ModalState.INVALID));
        }
      })
    ).subscribe());
  }

  createForm(): void {
    this.form = new UntypedFormGroup({pendingDocuments: new UntypedFormArray([]), uploadedDocuments: new UntypedFormArray([])});
  }

  override ngOnDestroy(): void {
    this.destroyed = true;
    this.stopReads.next();
    this.stopReads.complete();
    super.ngOnDestroy();
  }

  private emitValidState(): void {
      this.subject.next(new ModalStateEvent(ModalState.VALID));
  }

  private emitProcessEndState(): void {
    this.subject.next(new ModalStateEvent(ModalState.PROCESS_END));
  }

  private emitSuccessState(): void {
    this.subject.next(new ModalStateEvent(ModalState.SUCCESS));
  }

  private emitPendingState(): void {
    this.subject.next(new ModalStateEvent(ModalState.PENDING));
  }

  private subscribeParentModalState(): void {
    this.subscriptions.push(this.state.subscribe(modalState => {
      if (modalState.status === ModalState.HIDE) {
        this.destroyed = true;
        this.stopReads.next();
      } else if (modalState.status === ModalState.PENDING) {
        this.submit();
      }
    }));
  }

  private syncDescriptions(): void {
    [...(this.pendingDocuments?.controls || []), ...(this.uploadedDocuments?.controls || [])].forEach(control => {
      const {file, description, '@id': id} = control.getRawValue();
      const document = this.documentsAMs.find(candidate => id ? candidate['@id'] === id
        : file && this.getFileKey(candidate.file) === this.getFileKey(file));
      if (document) {
        document.description = description;
      }
    });
  }

  private submit(): void {
    if (this.destroyed || this.submitting) {
      return;
    }
    if (this.initializing || !this.form.valid) {
      this.emitProcessEndState();
      if (!this.initializing && this.form.valid) {
        this.emitValidState();
      }
      return;
    }
    this.syncDescriptions();
    this.documentErrorKey = null;
    this.submitting = true;
    let succeeded = false;
    this.subscriptions.push(defer(() => this.documentUploader.uploadDocument(this.documentsAMs)).pipe(
      switchMap(({documents$, progress$}) => merge(
        progress$.pipe(tap((progress: number) => this.uploadProgress = progress), ignoreElements()),
        documents$.pipe(
          switchMap(documents => {
            this.project.documents = documents.map(document => document['@id']);
            return this.projectRS.put(this.project).pipe(map(() => documents));
          }),
          switchMap(documents => documents.length
            ? forkJoin(documents.map(document => this.documentUploader.loadReadyDocument(document, 600)))
            : of([]))
        )
      )),
      takeUntil(this.stopReads),
      finalize(() => {
        this.submitting = false;
        if (!this.destroyed && !succeeded) {
          this.uploadProgress = null;
          this.patchForm();
          this.emitProcessEndState();
          if (this.form.valid) {
            this.emitValidState();
          }
        }
      })
    ).subscribe({
      next: documents => {
        succeeded = true;
        this.documentsAMs = documents;
        this.subject.next(new ModalStateEvent(ModalState.SUCCESS, documents));
      },
      error: error => {
        this.documentErrorKey = this.documentUploader.previewErrorKey(error);
        if (error instanceof HttpErrorResponse) {
          this.responseErrorsHandler.handleFormErrors(this.form, error);
        }
      }
    }));
  }

  choose(event, callback) {
    callback();
  }

  // onRemoveTemplatingFile(event, file, removeFileCallback, index) {
  //   removeFileCallback(event, index);
  //   this.totalSize -= parseInt(this.formatSize(file.size));
  //   this.totalSizePercent = this.totalSize / 10;
  //   this.documents.removeAt(index);
  // }

  // onRemoveTemplatingFile(event, file: File, removeFileCallback, index: number) {
  //   removeFileCallback(event, index);
  //   this.totalSize -= file.size;
  //   this.totalSizePercent = (this.totalSize / (1024 * 1024)) * 100;
  //
  //   // usuń z FormArray i z listy AM
  //   this.removeDocument(index);
  //
  //   // usuń fingerprint
  //   this.fileKeys.delete(this.getFileKey(file));
  // }

  onClearTemplatingUpload(clear) {
    clear();
    this.totalSize = 0;
    this.totalSizePercent = 0;
  }

  onTemplatedUpload() {
    this.messageService.add({ severity: 'info', summary: 'Success', detail: 'File Uploaded', life: 3000 });
  }
  //
  // onSelectedFiles(event) {
  //   this.files = event.currentFiles;
  //   console.log(event);
  //   new Promise<void>((resolve) => {
  //     this.files.forEach(async (file) => {
  //       this.totalSize += parseInt(this.formatSize(file.size));
  //       if (file.type === 'application/pdf') {
  //         file.thumbnail = await this.pdfThumbnail.generateThumbnail(file, 600);
  //       }
  //
  //       console.log(this.documentsAMs.find(e => e.file === file));
  //       if (!this.documentsAMs.find(e => e.file === file)) {
  //         console.log('test');
  //         const documentAM = new DocumentApiModel();
  //         documentAM.file = file;
  //         this.documentsAMs.push(documentAM);
  //       }
  //     });
  //     this.totalSizePercent = this.totalSize / 10;
  //     resolve();
  //   }).then(() => this.patchForm());
  // }

  // async onSelectedFiles(event: any): Promise<void> {
  //   const selected: File[] = event?.currentFiles ?? event?.files ?? [];
  //   if (!this.documentsAMs) this.documentsAMs = [];
  //
  //   // tylko nowe (po kluczu), nawet jeśli obiekt File jest inny
  //   const newFiles = selected.filter(f => {
  //     const key = this.getFileKey(f);
  //     if (this.fileKeys.has(key)) return false;
  //     this.fileKeys.add(key);
  //     return true;
  //   });
  //
  //   let bytesAdded = 0;
  //
  //   await Promise.all(
  //     newFiles.map(async (file) => {
  //       const doc = new DocumentApiModel();
  //       doc.file = file;
  //
  //       if (file.type === 'application/pdf' || file.type.startsWith('application/pdf')) {
  //         try {
  //           (file as any).thumbnail = await this.pdfThumbnail.generateThumbnail(file, 600);
  //         } catch (e) {
  //           console.error('PDF thumbnail error', e);
  //         }
  //       }
  //
  //       this.documentsAMs.push(doc);
  //       bytesAdded += file.size;
  //     })
  //   );
  //
  //   this.totalSize += bytesAdded;
  //   this.totalSizePercent = (this.totalSize / (1024 * 1024)) * 100;
  //
  //   this.patchForm();
  // }

  uploadEvent(callback) {
    callback();
  }

  clearEvent(callback ) {
    this.pendingDocuments.clear();
    this.documentsAMs = this.documentsAMs.filter(e => e["@id"])
    callback();
  }

  formatSize(bytes) {
    const k = 1024;
    const dm = 3;
    const sizes = [
      'common.b',
      'common.B',
      'common.KB',
      'common.MB',
      'common.GB',
      'common.TB',
    ];
    if (bytes === 0) {
      return `0 ${this.translate.instant(sizes[0])}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${this.translate.instant(sizes[i])}`;
  }

  createDocumentsGroup(documentAM: DocumentApiModel = null): UntypedFormGroup {
    return this.fb.group({
      file: new UntypedFormControl(documentAM.file),
      description: new UntypedFormControl(documentAM?.description ? documentAM.description : '', [
        RxwebValidators.minLength({value: 2}),
        RxwebValidators.maxLength({value: 255}),
      ]),
      createdAt: new UntypedFormControl(documentAM.createdAt ? format(documentAM.createdAt, 'dd.MM.yyyy'): null),
      '@id': new UntypedFormControl(documentAM['@id']),
      originalName: new UntypedFormControl(documentAM.originalName || documentAM.file?.name || documentAM.name),
      size: new UntypedFormControl(documentAM.file?.size || documentAM.size || 0)
    });
  }

  addPendingDocument(documentAM: DocumentApiModel = null): void {
    const group = this.createDocumentsGroup(documentAM);
    this.pendingDocuments.push(group);
  }

  addUploadedDocument(documentAM: DocumentApiModel = null): void {
    const group = this.createDocumentsGroup(documentAM);
    this.uploadedDocuments.push(group);
  }

  removeDocument(index: number): void {
    this.pendingDocuments.removeAt(index);
  }

  private patchForm(): void {
    this.syncDescriptions();
    this.pendingDocuments.clear();
    this.uploadedDocuments.clear();
    this.documentsAMs.forEach(document => document['@id'] ? this.addUploadedDocument(document) : this.addPendingDocument(document));
    this.totalSize = this.documentsAMs.reduce((total, document) => total + (document.file?.size || Number(document.size) || 0), 0);
    this.totalSizePercent = this.totalSize / 10;
  }

  private getFileKey(file: File | undefined): string {
    return file ? `${file.name}|${file.size}|${file.type}|${file.lastModified}` : '';
  }

  // private getFileKey = (f: File) =>
  //   `${f.name}|${f.size}|${f.type}|${f.lastModified}`;

  async onSelectedFiles(event: any): Promise<void> {
    const selected: File[] = event?.currentFiles ?? [];
    const newFiles = selected.filter(file => !this.documentsAMs.some(document => this.getFileKey(document.file) === this.getFileKey(file)));
    try {
      const documents = await Promise.all(newFiles.map(async file => {
        const document = new DocumentApiModel();
        document.file = file;
        if (file.type === 'application/pdf') {
          (file as File & {thumbnail?: string}).thumbnail = await this.pdfThumbnail.generateThumbnail(file, 600);
        }
        return document;
      }));
      if (!this.destroyed) {
        this.documentsAMs.unshift(...documents);
        this.patchForm();
      }
    } catch (error) {
      if (!this.destroyed) {
        this.documentErrorKey = this.documentUploader.previewErrorKey(error);
        this.emitProcessEndState();
        this.emitValidState();
      }
    }
  }

  onRemoveTemplatingFile(event: Event, control: AbstractControl, removeFileCallback: (...args: unknown[]) => unknown, index?: number): void {
    event.stopPropagation();
    const id = control.get('@id').value;
    const file = control.get('file').value;
    const document = this.documentsAMs.find(candidate => id ? candidate['@id'] === id
      : this.getFileKey(candidate.file) === this.getFileKey(file));
    const remove = () => {
      this.documentsAMs = this.documentsAMs.filter(candidate => candidate !== document);
      // Discard the removed row before synchronizing the remaining form values.
      const group = id ? this.uploadedDocuments : this.pendingDocuments;
      const row = group.controls.indexOf(control);
      if (row >= 0) {
        group.removeAt(row);
      }
      this.patchForm();
    };
    if (id) {
      this.subscriptions.push(this.documentUploader.deleteDocumentForProject(id, this.project).pipe(takeUntil(this.stopReads)).subscribe({
        next: remove,
        error: error => this.responseErrorsHandler.handleFormErrors(this.form, error)
      }));
    } else {
      remove();
    }
  }
}
